export const SettingsRoot = 'settings' as const;

export const SettingsGroup = {
  User: 'user',
  Account: 'account',
  Platform: 'platform',
} as const;

export type SettingsGroup = (typeof SettingsGroup)[keyof typeof SettingsGroup];

export const UserSettingsSection = {
  General: 'general',
  Integrations: 'integrations',
  Memories: 'memories',
} as const;

export type UserSettingsSection = (typeof UserSettingsSection)[keyof typeof UserSettingsSection];

export const UserMcpSubpage = {
  Claude: 'claude',
  ChatGpt: 'chatgpt',
  Tokens: 'tokens',
} as const;

export type UserMcpSubpage = (typeof UserMcpSubpage)[keyof typeof UserMcpSubpage];

export const AccountSettingsSection = {
  Account: 'account',
  Users: 'users',
  Groups: 'groups',
  Models: 'models',
  DataAgents: 'data-agents',
  Features: 'features',
  Usage: 'usage',
  AuditLog: 'audit-log',
  AvatarGallery: 'avatar-gallery',
  A2A: 'a2a',
} as const;

export type AccountSettingsSection = (typeof AccountSettingsSection)[keyof typeof AccountSettingsSection];

export const PlatformSettingsSection = {
  Usage: 'usage',
  Accounts: 'accounts',
} as const;

/** Nested platform-account id. Must not reuse `accountId` — that param is the workspace. */
export const PlatformAccountIdParam = 'platformAccountId' as const;
export const platformAccountIdPath = `:${PlatformAccountIdParam}` as const;

export type PlatformSettingsSection = (typeof PlatformSettingsSection)[keyof typeof PlatformSettingsSection];

export const UsageSubpath = {
  Users: 'users',
  Pricing: 'pricing',
} as const;

export const PlatformAccountSubpage = {
  Features: 'features',
  Controls: 'controls',
  UsageLimits: 'usage-limits',
  Storage: 'storage',
  DataAgents: 'data-agents',
  LinkedOrganizations: 'linked-organizations',
} as const;

export type PlatformAccountSubpage = (typeof PlatformAccountSubpage)[keyof typeof PlatformAccountSubpage];

export type UsageSubpath = (typeof UsageSubpath)[keyof typeof UsageSubpath];

/** URL segment for a provisioned semantic-model entitlement. */
export const ModelKind = {
  PowerBi: 'powerbi',
  Rosetta: 'malloy',
} as const;

export type ModelKind = (typeof ModelKind)[keyof typeof ModelKind];

export const AccountSettingsFormPageId = {
  Account: 'account',
  Models: 'models',
  ModelSwitching: 'model-switching',
  Features: 'features',
  UsageLimits: 'usage-limits',
} as const;

export type AccountSettingsFormPageId = (typeof AccountSettingsFormPageId)[keyof typeof AccountSettingsFormPageId];

export const AccountModelsSubpage = {
  InChatSwitching: 'in-chat-model-switching',
} as const;

export type AccountModelsSubpage = (typeof AccountModelsSubpage)[keyof typeof AccountModelsSubpage];

function trimAccountPath(accountPath: string): string {
  return accountPath.replace(/\/+$/, '');
}

/** Relative to the settings root, e.g. "user/general". */
export function settingsSegment(group: SettingsGroup, section: string, ...parts: string[]): string {
  return [group, section, ...parts].join('/');
}

export function userSettingsSegment(section: UserSettingsSection): string {
  return settingsSegment(SettingsGroup.User, section);
}

export function accountSettingsSegment(section: AccountSettingsSection, ...parts: string[]): string {
  return settingsSegment(SettingsGroup.Account, section, ...parts);
}

export function platformSettingsSegment(section: PlatformSettingsSection, ...parts: string[]): string {
  return settingsSegment(SettingsGroup.Platform, section, ...parts);
}

/** Absolute, e.g. "/a/123/settings/user/general". */
export function settingsSectionPath(accountPath: string, segment: string): string {
  return `${trimAccountPath(accountPath)}/${SettingsRoot}/${segment}`;
}

export function settingsPath(accountPath: string, section: UserSettingsSection = UserSettingsSection.General): string {
  return settingsSectionPath(accountPath, userSettingsSegment(section));
}

export function userMcpSubpagePath(accountPath: string, subpage: UserMcpSubpage): string {
  return settingsSectionPath(
    accountPath,
    settingsSegment(SettingsGroup.User, UserSettingsSection.Integrations, subpage)
  );
}

export function accountSectionPath(accountPath: string, section: AccountSettingsSection, ...parts: string[]): string {
  return settingsSectionPath(accountPath, accountSettingsSegment(section, ...parts));
}

export function platformSectionPath(accountPath: string, section: PlatformSettingsSection, ...parts: string[]): string {
  return settingsSectionPath(accountPath, platformSettingsSegment(section, ...parts));
}

export function accountSettingsPath(
  accountPath: string,
  section: AccountSettingsSection = AccountSettingsSection.Account
): string {
  return accountSectionPath(accountPath, section);
}

export function accountModelsSubpagePath(accountPath: string, subpage: AccountModelsSubpage): string {
  return accountSectionPath(accountPath, AccountSettingsSection.Models, subpage);
}

export function platformSettingsPath(
  accountPath: string,
  section: PlatformSettingsSection = PlatformSettingsSection.Usage
): string {
  return platformSectionPath(accountPath, section);
}

export function accountUsageUserPath(accountPath: string, userId: string): string {
  return accountSectionPath(accountPath, AccountSettingsSection.Usage, UsageSubpath.Users, encodeURIComponent(userId));
}

export function accountUserPath(accountPath: string, userId: string): string {
  return accountSectionPath(accountPath, AccountSettingsSection.Users, encodeURIComponent(userId));
}

export function accountGroupPath(accountPath: string, groupId: string): string {
  return accountSectionPath(accountPath, AccountSettingsSection.Groups, encodeURIComponent(groupId));
}

export function accountAuditLogEntryPath(accountPath: string, entryId: number): string {
  return accountSectionPath(accountPath, AccountSettingsSection.AuditLog, String(entryId));
}

export function platformAccountSubpagePath(
  accountPath: string,
  accountId: number,
  subpage: PlatformAccountSubpage
): string {
  return platformSectionPath(accountPath, PlatformSettingsSection.Accounts, String(accountId), subpage);
}

export function isModelKind(value: string | undefined): value is ModelKind {
  switch (value) {
    case ModelKind.PowerBi:
    case ModelKind.Rosetta:
      return true;
    default:
      return false;
  }
}

export function accountModelPath(accountPath: string, kind: ModelKind, modelId: string): string {
  return accountSectionPath(accountPath, AccountSettingsSection.DataAgents, kind, encodeURIComponent(modelId));
}

export function platformAccountModelPath(
  accountPath: string,
  accountId: number,
  kind: ModelKind,
  modelId: string
): string {
  return platformSectionPath(
    accountPath,
    PlatformSettingsSection.Accounts,
    String(accountId),
    PlatformAccountSubpage.DataAgents,
    kind,
    encodeURIComponent(modelId)
  );
}

export function platformUsageUserPath(accountPath: string, userId: string): string {
  return platformSectionPath(
    accountPath,
    PlatformSettingsSection.Usage,
    UsageSubpath.Users,
    encodeURIComponent(userId)
  );
}

export function platformUsagePricingPath(accountPath: string): string {
  return platformSectionPath(accountPath, PlatformSettingsSection.Usage, UsageSubpath.Pricing);
}

export function platformAccountsPath(accountPath: string): string {
  return platformSectionPath(accountPath, PlatformSettingsSection.Accounts);
}

export function platformAccountPath(accountPath: string, accountId: number): string {
  return platformSectionPath(accountPath, PlatformSettingsSection.Accounts, String(accountId));
}

const LegacyTab = {
  Profile: 'profile',
  Connections: 'connections',
} as const;

const LEGACY_SEGMENT_TARGETS: Record<string, string> = {
  integrations: userSettingsSegment(UserSettingsSection.Integrations),
  connectors: userSettingsSegment(UserSettingsSection.Integrations),
  'ai-memories': userSettingsSegment(UserSettingsSection.Memories),
  'ai-behavior': userSettingsSegment(UserSettingsSection.Memories),
};

const ACCOUNT_GENERAL_SUBPAGE_TARGETS: Record<string, string> = {
  'available-models': `${AccountSettingsSection.Models}/${AccountModelsSubpage.InChatSwitching}`,
  features: AccountSettingsSection.Features,
};

/**
 * Maps leftover flat settings URLs (`?tab=`, `/integrations`, `/ai-memories`)
 * onto a path relative to the settings root. Returns null when the segment is
 * already a known group so those routes can keep handling it.
 */
export function resolveLegacySettingsTarget(input: { segment: string | null; tab: string | null }): string | null {
  const segment = input.segment?.trim() || null;
  const tab = input.tab?.trim() || null;

  switch (true) {
    case segment === SettingsGroup.User || segment === SettingsGroup.Account || segment === SettingsGroup.Platform:
      return null;
    case segment != null && segment in LEGACY_SEGMENT_TARGETS:
      return LEGACY_SEGMENT_TARGETS[segment];
    case segment == null:
      switch (tab) {
        case LegacyTab.Connections:
          return userSettingsSegment(UserSettingsSection.Integrations);
        case LegacyTab.Profile:
        default:
          return userSettingsSegment(UserSettingsSection.General);
      }
    default:
      return userSettingsSegment(UserSettingsSection.General);
  }
}

/** Maps leftover `/account/general/:subpage` URLs onto split account sections. */
export function resolveAccountLeftover(input: { segment: string; subpage?: string | null }): string | null {
  switch (input.segment) {
    case 'general': {
      const subpage = input.subpage?.trim() ?? '';
      return subpage === ''
        ? AccountSettingsSection.Account
        : (ACCOUNT_GENERAL_SUBPAGE_TARGETS[subpage] ?? AccountSettingsSection.Account);
    }
    default:
      return null;
  }
}
